import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Printer, Send, Pencil, FileText, MessageCircle, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { EmailStatus } from '../components/EmailStatus';
import DispatchSupplierDialog from '../components/DispatchSupplierDialog';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com",
  paynow_uen: "202606027K"
};

export const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [doMap, setDoMap] = useState({});

  useEffect(() => {
    fetchInvoice();
    axios.get(`${BACKEND_URL}/api/invoices/${id}/covered-dos`)
      .then((res) => {
        const map = {};
        (res.data.delivery_orders || []).forEach((d) => { map[d.document_number] = d.id; });
        setDoMap(map);
      })
      .catch(() => {});
  }, [id]);

  const openDO = async (doNumber) => {
    if (doMap[doNumber]) return navigate(`/admin/billing/delivery-order/${doMap[doNumber]}`);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/delivery-orders/by-number/${encodeURIComponent(doNumber)}`);
      navigate(`/admin/billing/delivery-order/${res.data.id}`);
    } catch {
      toast.error(`Delivery order ${doNumber} not found`);
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Failed to load invoice');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/invoices/${id}/payment`, { payment_method: 'Manual' });
      toast.success('Invoice marked as paid');
      fetchInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const sendToCustomer = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/invoices/${id}/send`);
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send invoice'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h2>
          <Button onClick={() => navigate('/admin/billing')}>Back to Billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/billing')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{invoice.document_number}</h1>
                <p className="text-gray-600 mt-1">Invoice Details</p>
                {(invoice.created_by || invoice.updated_by) && (
                  <p className="text-xs text-gray-400 mt-0.5" data-testid="invoice-audit-info">
                    {invoice.created_by && `Created by ${invoice.created_by}${invoice.created_by_role ? ` (${invoice.created_by_role})` : ''}`}
                    {invoice.created_by && invoice.updated_by && ' • '}
                    {invoice.updated_by && `Last edited by ${invoice.updated_by}${invoice.updated_by_role ? ` (${invoice.updated_by_role})` : ''}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!invoice.paid && (
                <Button 
                  onClick={markAsPaid} 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={actionLoading}
                  data-testid="mark-paid-btn"
                >
                  <CheckCircle size={20} className="mr-2" />
                  Mark as Paid
                </Button>
              )}
              <Button 
                onClick={() => navigate(`/admin/billing/invoice/${id}/edit`)}
                variant="outline"
                data-testid="edit-invoice-btn"
              >
                <Pencil size={20} className="mr-2" />
                Edit
              </Button>
              <Button 
                onClick={sendToCustomer} 
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={sendingEmail}
                data-testid="send-invoice-btn"
              >
                {sendingEmail ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={20} />
                    Send to Customer
                  </span>
                )}
              </Button>
              <Button onClick={() => setShowPaymentModal(true)} variant="outline"
                      className="text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                      data-testid="record-payment-btn">
                <Receipt size={20} className="mr-2" />
                Record Customer Payment
              </Button>
              <Button onClick={() => setShowDispatchModal(true)} variant="outline"
                      className="text-emerald-700 border-emerald-600 hover:bg-emerald-50"
                      data-testid="dispatch-supplier-btn">
                <MessageCircle size={20} className="mr-2" />
                Dispatch to Supplier
              </Button>
              <Button asChild variant="outline" data-testid="preview-pdf-btn">
                <a href={`${BACKEND_URL}/api/invoices/${id}/pdf`} target="_blank" rel="noreferrer">
                  <FileText size={20} className="mr-2" />
                  Preview PDF
                </a>
              </Button>
              <Button onClick={handlePrint} variant="outline" data-testid="print-invoice-btn">
                <Printer size={20} className="mr-2" />
                Print
              </Button>
            </div>
          </div>
          <EmailStatus docType="invoice" docId={id} />
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="max-w-4xl mx-auto">
          <Card className="print:shadow-none print:border-none">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-green-600">{COMPANY_INFO.name}</h2>
                  <p className="text-gray-600 mt-1">{COMPANY_INFO.address}</p>
                  <p className="text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                  <p className="text-gray-600">Email: {COMPANY_INFO.email}</p>
                  {invoice.gst_reg_no && (
                    <p className="text-gray-600" data-testid="gst-reg-no">GST Reg No: {invoice.gst_reg_no}</p>
                  )}
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-gray-900">{(invoice.gst_amount || 0) > 0 ? 'TAX INVOICE' : 'INVOICE'}</h1>
                  {invoice.paid ? (
                    <Badge className="bg-green-100 text-green-800 mt-2" data-testid="paid-badge">
                      PAID{invoice.paid_at ? ` — ${new Date(invoice.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                    </Badge>
                  ) : (invoice.amount_paid > 0 && invoice.balance_due > 0) ? (
                    <Badge className="bg-orange-100 text-orange-800 border border-orange-300 mt-2" data-testid="partially-paid-badge">PARTIALLY PAID</Badge>
                  ) : null}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Bill To:</h3>
                  <p className="font-bold text-lg">{invoice.company_name || invoice.customer_name}</p>
                  {invoice.company_name && invoice.customer_name && (
                    <p className="text-gray-600">Attn: {invoice.customer_name}</p>
                  )}
                  <p className="text-gray-600">{invoice.customer_email}</p>
                  <p className="text-gray-600">{invoice.customer_phone}</p>
                  {invoice.customer_address && (
                    <p className="text-gray-600">{invoice.customer_address}</p>
                  )}
                  {invoice.po_number && (
                    <p className="text-gray-600 mt-2"><span className="font-semibold">P/O No:</span> {invoice.po_number}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-gray-600">Invoice No: </span>
                    <span className="font-semibold text-green-600" data-testid="invoice-detail-document-number">{invoice.document_number}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-gray-600">Invoice Date: </span>
                    <span className="font-semibold">{formatDate(invoice.invoice_date || invoice.created_at)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-gray-600">Payment Terms: </span>
                    <span className="font-semibold">{invoice.payment_terms || 'COD - Cash on Delivery'}</span>
                  </div>
                  {invoice.paid && invoice.paid_at && (
                    <div>
                      <span className="text-gray-600">Paid On: </span>
                      <span className="font-semibold text-green-600">{formatDate(invoice.paid_at)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-center w-24">DO No.</th>
                      <th className="border p-3 text-left">Description</th>
                      <th className="border p-3 text-center w-24">Lorry No</th>
                      <th className="border p-3 text-center w-16">Qty</th>
                      <th className="border p-3 text-center w-20">Hours</th>
                      <th className="border p-3 text-right w-28">Unit Price</th>
                      <th className="border p-3 text-right w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items.map((item, index) => (
                      <tr key={index}>
                        <td className="border p-3 text-center" data-testid={`invoice-item-do-${index}`}>{item.do_number ? <><button type="button" onClick={() => openDO(item.do_number)} className="text-green-700 font-medium underline hover:text-green-900 print:no-underline print:text-black" data-testid={`invoice-item-do-link-${index}`}>{item.do_number}</button>{item.do_date ? <span className="block text-xs text-gray-500">{item.do_date}</span> : null}</> : '-'}</td>
                        <td className="border p-3">{item.description}</td>
                        <td className="border p-3 text-center" data-testid={`invoice-item-lorry-${index}`}>{item.lorry_no || '-'}</td>
                        <td className="border p-3 text-center">{item.quantity}</td>
                        <td className="border p-3 text-center">{item.hours ? item.hours : '-'}</td>
                        <td className="border p-3 text-right">${item.unit_price.toFixed(2)}</td>
                        <td className="border p-3 text-right">${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end mb-8">
                <div className="w-64">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-semibold">${invoice.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-gray-600">GST ({invoice.gst_rate || 0}%):</span>
                    <span className="font-semibold">${invoice.gst_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 text-lg">
                    <span className="font-bold">Total Due:</span>
                    <span className="font-bold text-green-600">${invoice.total_amount.toFixed(2)}</span>
                  </div>
                  {(invoice.amount_paid > 0 || invoice.balance_due > 0) && (
                    <>
                      <div className="flex justify-between py-2 border-t border-gray-200 mt-2 pt-2">
                        <span className="text-gray-600">Amount Paid:</span>
                        <span className="font-semibold text-blue-600">${(invoice.amount_paid || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="font-semibold text-gray-700">Balance Due:</span>
                        <span className={`font-bold ${(invoice.balance_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ${(invoice.balance_due || 0).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {invoice.notes && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-700 mb-2">Notes:</h3>
                  <p className="text-gray-600">{invoice.notes}</p>
                </div>
              )}

              <div className="border-t pt-6 mt-6 bg-green-50 -mx-8 px-8 -mb-8 pb-8 rounded-b-lg print:bg-white">
                <h3 className="font-semibold text-gray-700 mb-3">Payment Information:</h3>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <p className="text-gray-600">PayNow UEN: <span className="font-semibold">{COMPANY_INFO.paynow_uen}</span></p>
                    <p className="text-sm text-gray-500 mt-1">Scan the QR code to pay via PayNow{!invoice.paid ? ' — amount and invoice reference are pre-filled' : ''}</p>
                  </div>
                  <img
                    src={!invoice.paid ? `${BACKEND_URL}/api/invoices/${invoice.id}/paynow-qr` : '/qr_code.jpeg'}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/qr_code.jpeg'; }}
                    alt="PayNow QR Code"
                    className="w-32 h-32 object-contain border border-gray-200 rounded bg-white p-1"
                    data-testid="paynow-qr"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dispatch to Supplier (auto-fills from Invoice) */}
      <DispatchSupplierDialog
        open={showDispatchModal}
        onOpenChange={setShowDispatchModal}
        sourceType="invoice"
        sourceId={invoice?.id || ''}
        sourceNumber={invoice?.invoice_number || ''}
        prefilledJob={{
          job_date: invoice?.service_date || invoice?.issue_date || '',
          pickup_location: invoice?.pickup_address || '',
          dropoff_location: invoice?.delivery_address || '',
          cargo: (invoice?.line_items || []).map(it => it.description).filter(Boolean).join(', '),
          customer_ref: `${invoice?.customer_name || ''} / ${invoice?.invoice_number || ''}`,
        }}
      />
    </div>
  );
};
